// CONTRACT:I4-POLICY-DECISION.1.0
/** Attribute FQN helpers — parse/build the OpenTDF FQN form and derive an object's
 *  bound attribute FQNs from its security labels.
 *
 *  FQN form: `https://{namespace}/attr/{name}/value/{value}` (case-insensitive).
 *  We parse with a plain string split (NOT `new URL()`) so the lib stays bundler-
 *  and worker-safe with zero DOM coupling. */

import type { PolicyBundle } from "./types.ts";
import { buildVocabMap, normalizeSecurityView, type VocabMap } from "./normalize.ts";

export interface ParsedFqn {
  namespace: string;
  name: string;
  value: string;
}

/** Parse `https://ns/attr/<name>/value/<value>` → parts, or null if malformed.
 *
 *  STRICT BY PARITY ANCHOR (corpus 2.7.0 `abac_decide`, vectors ad-0012/ad-0013 — filed
 *  2026-07-05): `https://`-only, and the value is NOT percent-decoded. The bound-FQN
 *  comparison must stay BYTE-EXACT to what the KAS authority emits — any client-side
 *  normalization here (scheme tolerance, `%XX` decode, path-segment forgiveness) can only
 *  ever WIDEN a permit relative to the KAS, i.e. REVEAL what the authority hides. So a
 *  non-https/schemeless FQN and a percent-encoded value both fail to parse/match, and
 *  evaluateAbac fail-closes the WHOLE object on an unparseable binding. Canonical twins:
 *  TDFLite `@tdflite/classification-schema` parseAttrFqn (JS, "decodes nothing") and
 *  go-tak-server `pdp_abac.go parseAttrFqn` (literal `https://` prefix, plain split).
 *  (History: this parser was scheme-agnostic + decodeURIComponent until 2026-07-05 —
 *  exactly the permissive shape ad-0012/ad-0013 were written to catch.) */
export function parseAttrFqn(fqn: string): ParsedFqn | null {
  if (!fqn.startsWith("https://")) return null;
  const noScheme = fqn.slice("https://".length);
  const slash = noScheme.indexOf("/");
  if (slash <= 0) return null; // no path, or empty namespace ("https:///…")
  const namespace = noScheme.slice(0, slash);
  const parts = noScheme.slice(slash + 1).split("/").filter(Boolean);
  // Expected EXACTLY: ["attr", "<name>", "value", "<value>"] (filter tolerates a lone
  // trailing slash; extra path segments beyond the value do NOT parse).
  if (parts.length === 4 && parts[0] === "attr" && parts[2] === "value") {
    return { namespace, name: parts[1], value: parts[3] };
  }
  return null;
}

/** Build the FQN for a namespace/attribute/value. The value is written RAW (no percent-
 *  encoding), mirroring the no-decode parse above so parse(build(v)).value === v for the
 *  bundle vocabulary. Callers only ever build from bundle-vocab values (deriveBoundFqns
 *  routes marking values through the bundle first), which are plain tokens by
 *  construction — a value that would need escaping is out-of-vocabulary and would never
 *  match an attribute anyway (fail-closed downstream). */
export function buildAttrFqn(namespace: string, name: string, value: string): string {
  return `https://${namespace}/attr/${name}/value/${value}`;
}

/** The subset of CanonicalEnvelope.security the PDP reads. (We avoid importing the
 *  schema package so abac-pdp stays dependency-free; the COP passes this shape.) */
export interface SecurityView {
  classification?: string;
  releasability?: string[];
  caveats?: string[];
  mission?: string[];
}

/** Derive an object's BOUND attribute FQNs from its security labels, DATA-DRIVEN:
 *  for each marking value, find the bundle attribute whose `values` list contains
 *  it, and bind that attribute=value. This auto-routes SECRET→classification_level,
 *  USA→releasable_to, SI→sci_control_system, NOFORN→foreign_release_prohibition,
 *  etc. — no hand-maintained field→attribute map.
 *
 *  A marking value matched by no attribute is IGNORED (not policy-relevant). An
 *  object with no recognised markings yields `[]` → permitted (truly unbound, e.g.
 *  a broadly-releasable UNCLASSIFIED object that binds nothing). */
/** Marking tokens may be prefixed in some datasets (e.g. mission `sci:SI`,
 *  `compartment:SI-G`, `sap:WASP`) — strip the known prefix before routing. */
const MARKING_PREFIX = /^(sci|compartment|sap|accm|dissem|control|rel|caveat)\s*:/i;
const stripPrefix = (t: string): string => String(t).replace(MARKING_PREFIX, "").trim();

/** Per-bundle cache of the informal→canonical normalization map. Keyed on the bundle
 *  OBJECT (a live-fetched bundle is a stable reference for its lifetime), so we build
 *  the closed vocab map once per bundle, not once per object over the whole corpus. */
const VOCAB_CACHE = new WeakMap<PolicyBundle, VocabMap>();
function vocabFor(bundle: PolicyBundle): VocabMap {
  let m = VOCAB_CACHE.get(bundle);
  if (!m) {
    m = buildVocabMap(bundle);
    VOCAB_CACHE.set(bundle, m);
  }
  return m;
}

export function deriveBoundFqns(security: SecurityView, bundle: PolicyBundle): string[] {
  // NORMALIZE FIRST (CONTRACT:I2-MARKING-NORMALIZER.1.0): rescue known informal markings
  // (e.g. caveat "REL:FVEY" → "FVEY", "SAP ALPHA" → "ALPHA") to their canonical bundle
  // values so they bind to the right attribute instead of being silently dropped.
  // FAIL-SAFE: only KNOWN canonical keys are mapped; unknown tokens pass through
  // unchanged (still unmatched below, still dropped) — never invented, never widened.
  security = normalizeSecurityView(security, vocabFor(bundle));

  const markings: string[] = [];
  if (security.classification) markings.push(security.classification);
  for (const r of security.releasability ?? []) markings.push(r);
  for (const c of security.caveats ?? []) markings.push(c);
  // `mission` may carry SCI/compartment markings (e.g. "sci:SI", "compartment:SI-G")
  // — strip the prefix and bind any that resolve to an attribute (a free-text op name
  // resolves to nothing and is ignored). This is what makes the multi-INT entity graph
  // clearance-shaped (the crown-jewel edge's SI-G binding).
  for (const m of security.mission ?? []) markings.push(stripPrefix(m));

  const fqns = new Set<string>();
  for (const value of markings) {
    if (!value) continue;
    const lc = value.toLowerCase();
    const def = bundle.attributes.find((a) => a.values.some((x) => x.toLowerCase() === lc));
    if (def) fqns.add(buildAttrFqn(bundle.namespace, def.name, value));
  }
  return [...fqns];
}
